import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, Chip, CircularProgress, Collapse, FormControl, InputLabel,
  Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Select, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import {
  ArrowBack, Cable, CheckCircle, ErrorOutline, PrecisionManufacturing,
  Close, ExpandLess, ExpandMore, Print, Refresh, Save, Scale, Science, WbSunny, NightsStay
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import api from '../api/authApi';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { canUser } from '../utils/permissions';
import machineParsers from '../utils/machineParsers';
import {
  connectPrinter, disconnectPrinter, getPrinterName,
  isBluetoothSupported, isPrinterConnected, printCollectionSlip
} from '../utils/bluetoothPrinter';
import { getReceiptSettings, printCollectionReceipt } from '../utils/collectionReceipt';

const { parseAnalyzerData, parseLiterData, extractLiterFrames, validReading, buildScaleZeroBytes } = machineParsers;

const fmt = (value, digits = 2) => Number(value || 0).toFixed(digits);
const today = () => new Date().toISOString().slice(0, 10);
const automaticShift = () => new Date().getHours() < 12 ? 1 : 2;

const MACHINE_PROFILES = [
  { value:'ecomilk', label:'EcoMilk FAT / SNF Analyzer', shortLabel:'EcoMilk' },
  { value:'lacto', label:'LACTO FAT / SNF Analyzer', shortLabel:'LACTO' },
  { value:'combined', label:'Combined AMCS Machine (Liter + FAT + SNF)', shortLabel:'Combined AMCS' },
];

const CONNECTION_MODES = [
  { value:'weight', label:'Liter / Weight Machine Only', help:'Liter automatic; FAT and SNF entered manually.' },
  { value:'analyzer', label:'FAT / SNF Analyzer Only', help:'FAT and SNF automatic; liter entered manually.' },
  { value:'both', label:'Both Separate Machines', help:'Liter, FAT and SNF are all automatic.' },
  { value:'combined', label:'One Combined AMCS Machine', help:'One port supplies liter, FAT and SNF.' },
];

const BAUD_RATES = [2400, 4800, 9600, 19200, 38400, 115200];

function findNearestGridRate(gridJson, fatValue, snfValue) {
  try {
    const grid = typeof gridJson === 'string' ? JSON.parse(gridJson) : gridJson;
    const fatKeys = Object.keys(grid?.rows || {}).filter(key => Number.isFinite(Number(key)));
    const snfKeys = (grid?.colHeaders || []).map(String).filter(key => Number.isFinite(Number(key)));
    if (!fatKeys.length || !snfKeys.length) return null;
    const nearest = (keys, target) => keys.reduce((best, key) =>
      Math.abs(Number(key) - target) < Math.abs(Number(best) - target) ? key : best);
    const fatKey = nearest(fatKeys, Number(fatValue));
    const snfKey = nearest(snfKeys, Number(snfValue));
    // Only bridge one normal 0.1 chart step; never guess a distant rate.
    if (Math.abs(Number(fatKey) - Number(fatValue)) > 0.101 || Math.abs(Number(snfKey) - Number(snfValue)) > 0.101) return null;
    const rate = Number(grid.rows?.[fatKey]?.[snfKey]);
    return rate > 0 ? { rate, fatKey, snfKey } : null;
  } catch {
    return null;
  }
}

function ReadingTile({ label, value, rawValue, unit, ready, icon, editable = false, disabled = false, onChange, onKeyDown, inputRef, step = '0.1' }) {
  return (
    <Box sx={{ p:1.25, borderRadius:2.5, background:ready?'#ecfdf5':'#f8fafc', border:`2px solid ${ready?'#86efac':'#e2e8f0'}`, minHeight:{ xs:96, lg:82 } }}>
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:0.6 }}>
        <Typography sx={{ color:'#64748b', fontWeight:800, fontSize:'0.78rem', letterSpacing:0.5 }}>{label}</Typography>
        <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
          {editable && <Chip label="Manual" size="small" color="warning" variant="outlined" sx={{ height:19, fontSize:'0.62rem' }}/>} 
          {icon}
        </Box>
      </Box>
      {editable ? (
        <TextField
          inputRef={inputRef}
          type="number"
          value={rawValue ?? ''}
          onChange={onChange}
          onKeyDown={onKeyDown}
          disabled={disabled}
          placeholder="Enter"
          size="small"
          fullWidth
          inputProps={{ min:0, step, inputMode:'decimal' }}
          InputProps={{ endAdornment:<Typography sx={{ fontSize:'0.72rem', fontWeight:800, color:'#64748b' }}>{unit}</Typography> }}
          sx={{ '& input':{ py:0.45, px:0.8, fontSize:'1.55rem', fontWeight:900, color:ready?'#166534':'#334155' } }}
        />
      ) : (
        <Typography sx={{ fontSize:{ xs:'1.7rem', md:'1.9rem' }, fontWeight:900, color:ready?'#166534':'#94a3b8', lineHeight:1 }}>
          {ready ? value : '—'} <span style={{ fontSize:'0.75rem', fontWeight:700 }}>{unit}</span>
        </Typography>
      )}
    </Box>
  );
}

function CustomerDisplay({
  farmerCode, farmerName, liter, fat, snf, density, rate, amount,
  onClear, onClearPorts, onConnectPrinter, serialSupported,
  printerStatus, printerName, printerMode
}) {
  const rows = [
    { label:'CODE', value:farmerCode || '—', digits:null },
    { label:'LITER', value:validReading(liter,0.001,9999) ? fmt(liter,3) : '—' },
    { label:'FAT', value:validReading(fat,1,15) ? fmt(fat,2) : '—' },
    { label:'SNF', value:validReading(snf,1,15) ? fmt(snf,2) : '—' },
    { label:'DENSITY', value:validReading(density,0,100) ? fmt(density,2) : '—' },
    { label:'RATE', value:Number(rate)>0 ? fmt(rate,2) : '—' },
    { label:'TOTAL', value:Number(amount)>0 ? fmt(amount,2) : '—' },
  ];

  return (
    <Box sx={{ position:{ xl:'sticky' }, top:{ xl:84 }, alignSelf:'start' }}>
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:1, mb:0.7 }}>
        <Typography sx={{ fontWeight:900 }}>2. Customer Display</Typography>
        <Box sx={{ display:'flex', alignItems:'center', gap:0.75, flexWrap:'wrap', justifyContent:'flex-end' }}>
          {printerMode === 'windows' ? (
            <Chip size="small" icon={<Print/>} color="success" variant="outlined" label="Windows default printer" />
          ) : printerStatus === 'connected' ? (
            <Chip size="small" icon={<Print/>} color="success" variant="outlined" label={printerName || 'Printer connected'} />
          ) : (
            <Button
              size="small"
              variant="contained"
              startIcon={<Print/>}
              onClick={onConnectPrinter}
              disabled={printerStatus === 'connecting'}
            >
              {printerStatus === 'connecting' ? 'Connecting…' : 'Connect Printer'}
            </Button>
          )}
          <Button size="small" variant="outlined" startIcon={<Refresh/>} onClick={onClear}>Clear Readings</Button>
          <Button size="small" variant="outlined" color="error" startIcon={<Cable/>} onClick={onClearPorts} disabled={!serialSupported}>
            Clear All Ports
          </Button>
        </Box>
      </Box>
      <Box sx={{
        p:1, borderRadius:2.5, background:'#050505',
        border:'6px solid #e11d2e', boxShadow:'0 14px 34px rgba(15,23,42,0.28)',
        width:'100%', maxWidth:'none', mx:'auto', overflow:'hidden'
      }}>
        {rows.map((row, index) => (
          <Box key={row.label} sx={{
            display:'grid', gridTemplateColumns:'minmax(125px,0.42fr) minmax(0,1fr)',
            minHeight:{ xs:70, lg:'clamp(66px,8.4vh,94px)' }, borderBottom:index===rows.length-1?'none':'3px solid #ef233c'
          }}>
            <Box sx={{
              display:'flex', alignItems:'center', justifyContent:'flex-start', px:{ xs:1.5, sm:2 },
              background:'#fff59d', borderRight:'3px solid #ef233c'
            }}>
              <Typography sx={{
                color:'#1f2937', fontFamily:'Arial Narrow, Arial, sans-serif', fontWeight:900,
                fontSize:{ xs:'1.2rem', sm:'1.55rem', lg:'clamp(1.35rem,1.75vw,1.8rem)' }, letterSpacing:0.3
              }}>{row.label}</Typography>
            </Box>
            <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', px:{ xs:1.5, sm:2.5 }, minWidth:0, textAlign:'center' }}>
              <Typography sx={{
                color:'#fff', fontFamily:'Arial Narrow, Arial, sans-serif', fontWeight:900,
                fontSize:{ xs:'2.25rem', sm:'2.85rem', lg:'clamp(2.45rem,3.4vw,3.8rem)' }, lineHeight:1,
                whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', fontVariantNumeric:'tabular-nums'
              }}>{row.value}</Typography>
            </Box>
          </Box>
        ))}
        <Box sx={{
          minHeight:{ xs:78, lg:76 }, mt:0.75, px:2, py:1, display:'flex', flexDirection:'column',
          alignItems:'center', justifyContent:'center', textAlign:'center',
          background:'linear-gradient(135deg,#0284c7,#0ea5e9 48%,#fde047 49%,#facc15)',
          borderRadius:1
        }}>
          <Typography sx={{ color:'#fff', fontWeight:900, fontSize:'1.05rem', textShadow:'0 1px 3px rgba(0,0,0,.7)' }}>
            {farmerName || 'Enter Farmer Code'}
          </Typography>
          <Typography sx={{ color:'#fff', fontWeight:700, fontSize:'0.74rem', textShadow:'0 1px 3px rgba(0,0,0,.7)' }}>
            LIVE MILK COLLECTION
          </Typography>
        </Box>
      </Box>
      <Typography sx={{ mt:1.2, color:'#64748b', fontSize:'0.73rem', textAlign:'center' }}>
        Values update automatically when the machine sends a reading.
      </Typography>
    </Box>
  );
}

export default function AutomaticCollection() {
  const { theme, t, user } = useApp();
  const { user: authUser } = useAuth();
  const canAddCollection = canUser(authUser, 'Collection', 'add');
  const navigate = useNavigate();
  const [profile, setProfile] = useState(() => {
    const savedMode = localStorage.getItem('dp_machine_connection_mode');
    const savedProfile = localStorage.getItem('dp_machine_profile') || 'ecomilk';
    if (savedMode === 'combined') return 'combined';
    return savedProfile === 'combined' ? 'ecomilk' : savedProfile;
  });
  const [connectionMode, setConnectionMode] = useState(() =>
    localStorage.getItem('dp_machine_connection_mode') ||
    (localStorage.getItem('dp_machine_profile') === 'combined' ? 'combined' : 'both'));
  const [setupExpanded, setSetupExpanded] = useState(false);
  const [setupWizardOpen, setSetupWizardOpen] = useState(() => {
    if (localStorage.getItem('dp_machine_setup_complete') === '1') return false;
    const savedMode = localStorage.getItem('dp_machine_connection_mode') ||
      (localStorage.getItem('dp_machine_profile') === 'combined' ? 'combined' : 'both');
    const needsAnalyzer = savedMode !== 'weight';
    const needsScale = savedMode === 'weight' || savedMode === 'both';
    return !((!needsAnalyzer || localStorage.getItem('dp_analyzer_port_slot')) &&
      (!needsScale || localStorage.getItem('dp_scale_port_slot')));
  });
  const [analyzerBaud, setAnalyzerBaud] = useState(Number(localStorage.getItem('dp_analyzer_baud')) || 9600);
  const [scaleBaud, setScaleBaud] = useState(Number(localStorage.getItem('dp_scale_baud')) || 9600);
  const [scaleZeroCommand, setScaleZeroCommand] = useState(() => {
    const saved = localStorage.getItem('dp_scale_zero_command');
    // This scale's confirmed working command is lowercase ASCII z followed by CR.
    // Migrate browsers that retained the incorrect raw-F2 setting.
    return !saved || ['f2', '0xf2', '242'].includes(saved.toLowerCase()) ? 'z' : saved;
  });
  const [printerType, setPrinterType] = useState(() => localStorage.getItem('dp_printer_type') || '2inch');
  const [printerMode, setPrinterMode] = useState(() => localStorage.getItem('dp_printer_mode') || 'windows');
  const [printerStatus, setPrinterStatus] = useState(() => isPrinterConnected() ? 'connected' : 'disconnected');
  const [printerName, setPrinterName] = useState(() => getPrinterName() || '');
  const [analyzerStatus, setAnalyzerStatus] = useState('disconnected');
  const [scaleStatus, setScaleStatus] = useState('disconnected');
  const [zeroingScale, setZeroingScale] = useState(false);
  const [rawAnalyzer, setRawAnalyzer] = useState('');
  const [rawScale, setRawScale] = useState('');
  const [farmerCode, setFarmerCode] = useState('');
  const [farmer, setFarmer] = useState(null);
  const [loadingFarmer, setLoadingFarmer] = useState(false);
  const [date, setDate] = useState(today());
  const [shift, setShift] = useState(automaticShift());
  const [milkTypeId, setMilkTypeId] = useState(1);
  const [liter, setLiter] = useState(null);
  const [fat, setFat] = useState(null);
  const [snf, setSnf] = useState(null);
  const [density, setDensity] = useState(null);
  const [rate, setRate] = useState(null);
  const [rateStatus, setRateStatus] = useState('Waiting for FAT and SNF');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [readingConfirmed, setReadingConfirmed] = useState(false);
  const [recentCollections, setRecentCollections] = useState([]);
  const [allFarmers, setAllFarmers] = useState([]);
  const [progressView, setProgressView] = useState('pending');

  const farmerInputRef = useRef(null);
  const literInputRef = useRef(null);
  const fatInputRef = useRef(null);
  const snfInputRef = useRef(null);
  const saveButtonRef = useRef(null);
  const searchTimerRef = useRef(null);
  const analyzerPortRef = useRef(null);
  const scalePortRef = useRef(null);
  const analyzerReaderRef = useRef(null);
  const scaleReaderRef = useRef(null);
  const mountedRef = useRef(true);
  const farmerRef = useRef(null);
  const readingSnapshotRef = useRef({ liter:null, fat:null, snf:null, density:null });
  const readingConfirmedRef = useRef(false);
  const autoConnectAttemptedRef = useRef(false);
  const zeroingScaleRef = useRef(false);

  const selectedProfile = MACHINE_PROFILES.find(x => x.value === profile) || MACHINE_PROFILES[0];
  const selectedConnectionMode = CONNECTION_MODES.find(item => item.value === connectionMode) || CONNECTION_MODES[2];
  const needsAnalyzerPort = connectionMode !== 'weight';
  const needsScalePort = connectionMode === 'weight' || connectionMode === 'both';
  const centerName = user?.centerName || 'Dairy Center';
  const serialSupported = typeof navigator !== 'undefined' && 'serial' in navigator;
  const amount = Number(liter || 0) * Number(rate || 0);
  const allReadingsReady = validReading(liter, 0.001, 9999) && validReading(fat, 1, 15) && validReading(snf, 1, 15);
  const manualLiterEntry = connectionMode === 'combined' ? analyzerStatus !== 'connected' :
    needsScalePort ? scaleStatus !== 'connected' : true;
  const manualAnalyzerEntry = needsAnalyzerPort ? analyzerStatus !== 'connected' : true;
  const manualEntryActive = manualLiterEntry || manualAnalyzerEntry;
  const entryConfirmed = allReadingsReady && (manualEntryActive || readingConfirmed);

  const changeManualReading = (field, setter) => (event) => {
    const value = event.target.value;
    setter(value === '' ? null : value);
    readingSnapshotRef.current = { ...readingSnapshotRef.current, [field]:value === '' ? null : Number(value) };
    setError('');
  };

  const resetStabilityCapture = useCallback(() => {
    readingSnapshotRef.current = { liter:null, fat:null, snf:null, density:null };
    readingConfirmedRef.current = false;
    setReadingConfirmed(false);
  }, []);

  useEffect(() => {
    farmerRef.current = farmer;
    resetStabilityCapture();
  }, [farmer, resetStabilityCapture]);

  useEffect(() => {
    mountedRef.current = true;
    setTimeout(() => farmerInputRef.current?.focus(), 100);
    return () => {
      mountedRef.current = false;
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      analyzerReaderRef.current?.cancel().catch(() => {});
      scaleReaderRef.current?.cancel().catch(() => {});
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('dp_machine_profile', profile);
    localStorage.setItem('dp_machine_connection_mode', connectionMode);
    localStorage.setItem('dp_analyzer_baud', String(analyzerBaud));
    localStorage.setItem('dp_scale_baud', String(scaleBaud));
    localStorage.setItem('dp_scale_zero_command', scaleZeroCommand);
    localStorage.setItem('dp_printer_type', printerType);
    localStorage.setItem('dp_printer_mode', printerMode);
  }, [profile, connectionMode, analyzerBaud, scaleBaud, scaleZeroCommand, printerType, printerMode]);

  const loadRecentCollections = useCallback(async () => {
    try {
      const [collectionResponse, farmerResponse] = await Promise.all([
        api.get('/collection', { params:{ date, pageSize:1000 } }),
        api.get('/farmer', { params:{ pageSize:1000 } }),
      ]);
      const rows = collectionResponse.data?.data?.data || [];
      setRecentCollections(rows.filter(row => Number(row.shift) === Number(shift)));
      const farmers = Array.isArray(farmerResponse.data?.data) ? farmerResponse.data.data : [];
      setAllFarmers(farmers.filter(item => item.isActive !== false && item.isActive !== 0));
    } catch {
      // Saving must remain available even if the recent grid cannot refresh.
    }
  }, [date, shift]);

  useEffect(() => {
    loadRecentCollections();
  }, [loadRecentCollections]);

  useEffect(() => {
    const bothMachinesReady = (!needsAnalyzerPort || analyzerStatus === 'connected') && (!needsScalePort || scaleStatus === 'connected');
    if (bothMachinesReady) {
      const timer = setTimeout(() => farmerInputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [analyzerStatus, scaleStatus, needsAnalyzerPort, needsScalePort]);

  const lookupFarmer = useCallback(async (code) => {
    const value = String(code || '').trim();
    if (!value) { farmerRef.current = null; setFarmer(null); return; }
    setLoadingFarmer(true);
    setError('');
    try {
      const res = await api.get('/farmer', { params:{ search:value, pageSize:10 } });
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      const match = list.find(x => String(x.farmerCode).trim().toLowerCase() === value.toLowerCase());
      if (!match) {
        farmerRef.current = null;
        setFarmer(null);
        setError(`Farmer code ${value} not found.`);
        return;
      }
      setFarmer(match);
      const farmerMilk = Number(match.milkTypeId);
      setMilkTypeId(farmerMilk === 2 ? 2 : 1);
      setTimeout(() => {
        if (manualLiterEntry) literInputRef.current?.focus();
        else if (manualAnalyzerEntry) fatInputRef.current?.focus();
        else saveButtonRef.current?.focus();
      }, 100);
    } catch (e) {
      farmerRef.current = null;
      setFarmer(null);
      setError(e.response?.data?.message || 'Could not load farmer details.');
    } finally { setLoadingFarmer(false); }
  }, [manualLiterEntry, manualAnalyzerEntry]);

  const handleFarmerChange = (event) => {
    const value = event.target.value.toUpperCase();
    setFarmerCode(value);
    farmerRef.current = null;
    setFarmer(null);
    setLiter(null); setFat(null); setSnf(null); setDensity(null); setRate(null);
    resetStabilityCapture();
    setSuccess('');
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (value.trim().length >= 3) searchTimerRef.current = setTimeout(() => lookupFarmer(value), 350);
  };

  useEffect(() => {
    if (!validReading(fat, 1, 15) || !validReading(snf, 1, 15)) {
      setRate(null);
      setRateStatus('Waiting for FAT and SNF');
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setRateStatus('Calculating rate...');
      try {
        const res = await api.get('/collection/rate', { params:{ milkTypeId, fat, snf } });
        const value = Number(res.data?.data);
        if (!cancelled && res.data?.success && value > 0) {
          setRate(value);
          setRateStatus(`${milkTypeId === 2 ? 'Buffalo' : 'Cow'} rate loaded`);
        } else if (!cancelled) {
          const chartResponse = await api.get(`/ratechart/${milkTypeId}`);
          const nearestRate = findNearestGridRate(chartResponse.data?.data?.gridJson, fat, snf);
          if (nearestRate) {
            setRate(nearestRate.rate);
            setRateStatus(`${milkTypeId === 2 ? 'Buffalo' : 'Cow'} boundary rate loaded using FAT ${nearestRate.fatKey}, SNF ${nearestRate.snfKey}`);
          } else {
            setRate(null);
            setRateStatus('No matching rate found');
          }
        }
      } catch (e) {
        if (!cancelled) {
          setRate(null);
          setRateStatus(e.response?.data?.message || 'Rate calculation failed');
        }
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [fat, snf, milkTypeId]);

  const trackStableReading = useCallback((snapshot, source) => {
    if (!farmerRef.current || readingConfirmedRef.current) return;
    // Separate analyzer/scale profiles confirm stability only from fresh
    // weighing-machine messages. A combined machine is counted from analyzer data.
    if (profile !== 'combined' && source !== 'scale') return;
    if (!validReading(snapshot.liter,0.001,9999) || !validReading(snapshot.fat,1,15) || !validReading(snapshot.snf,1,15)) {
      return;
    }
    const accepted = { ...snapshot };
    setLiter(accepted.liter);
    setFat(accepted.fat);
    setSnf(accepted.snf);
    setDensity(accepted.density);
    readingSnapshotRef.current = accepted;
    readingConfirmedRef.current = true;
    setReadingConfirmed(true);
    setTimeout(() => saveButtonRef.current?.focus(), 100);
  }, [profile]);

  const applyAnalyzerData = useCallback((text) => {
    setRawAnalyzer(text.slice(-180));
    if (!needsAnalyzerPort) return;
    if (!farmerRef.current || readingConfirmedRef.current) return;
    const readings = parseAnalyzerData(text, profile);
    const snapshot = { ...readingSnapshotRef.current };
    if (readings.liter != null) snapshot.liter = readings.liter;
    if (readings.fat != null) snapshot.fat = readings.fat;
    if (readings.snf != null) snapshot.snf = readings.snf;
    if (readings.density != null) snapshot.density = readings.density;
    readingSnapshotRef.current = snapshot;
    if (readings.liter != null) setLiter(readings.liter);
    if (readings.fat != null) setFat(readings.fat);
    if (readings.snf != null) setSnf(readings.snf);
    if (readings.density != null) setDensity(readings.density);
    trackStableReading(snapshot, 'analyzer');
  }, [profile, trackStableReading, needsAnalyzerPort]);

  const applyScaleData = useCallback((text) => {
    setRawScale(text.slice(-180));
    if (!needsScalePort) return;
    if (!farmerRef.current || readingConfirmedRef.current) return;
    // Match the working desktop code: only accept a complete packet longer
    // than 9 characters, take the value before "Lt", and convert it directly.
    if (String(text || '').length <= 9) return;
    const value = parseLiterData(text);
    if (value != null) {
      const snapshot = { ...readingSnapshotRef.current, liter:value };
      readingSnapshotRef.current = snapshot;
      setLiter(value);
      trackStableReading(snapshot, 'scale');
    }
  }, [trackStableReading, needsScalePort]);

  const readPort = async (port, kind) => {
    const decoder = new TextDecoder();
    let reader;
    try {
      reader = port.readable.getReader();
    } catch (e) {
      if (mountedRef.current) setError(`${kind === 'analyzer' ? 'Analyzer' : 'Scale'} reader could not start: ${e.message}`);
      return;
    }
    if (kind === 'analyzer') analyzerReaderRef.current = reader;
    else scaleReaderRef.current = reader;
    let buffer = '';
    let recoverableScaleError = false;
    try {
      while (mountedRef.current) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream:true });

        if (kind === 'scale') {
          const extracted = extractLiterFrames(buffer);
          if (extracted.frames.length) {
            extracted.frames.forEach(frame => applyScaleData(frame));
            buffer = extracted.remainder;
            if (buffer.length > 500) buffer = buffer.slice(-250);
            continue;
          }
        }

        const parts = buffer.split(/[\r\n]+/);
        buffer = parts.pop() || '';
        const packets = parts.length ? parts : (kind === 'scale' ? [] : (buffer.length >= 4 ? [buffer] : []));
        packets.forEach(packet => kind === 'analyzer' ? applyAnalyzerData(packet) : applyScaleData(packet));
        if (buffer.length > 500) buffer = buffer.slice(-250);
      }
    } catch (e) {
      recoverableScaleError = kind === 'scale' && /framing|parity|overrun|break/i.test(String(e.message || ''));
      if (mountedRef.current && !recoverableScaleError && e.name !== 'NetworkError') {
        setError(`${kind === 'analyzer' ? 'Analyzer' : 'Scale'} read error: ${e.message}`);
      }
    } finally {
      try { reader.releaseLock(); } catch {}
      const readerRef = kind === 'analyzer' ? analyzerReaderRef : scaleReaderRef;
      // An old cancelled read loop must never disconnect or overwrite a newer
      // reader created while the serial port is being reopened after zeroing.
      if (readerRef.current === reader) {
        readerRef.current = null;
        if (mountedRef.current && recoverableScaleError && scalePortRef.current === port) {
          // Some USB scale drivers report one transient framing error after the
          // zero command/reopen. Chrome creates a fresh readable stream; attach
          // a new reader instead of treating the machine as disconnected.
          setScaleStatus('connecting');
          setTimeout(() => {
            if (!mountedRef.current || scalePortRef.current !== port || scaleReaderRef.current) return;
            if (port.readable) {
              setScaleStatus('connected');
              void readPort(port, 'scale');
            } else {
              setScaleStatus('disconnected');
            }
          }, 250);
        } else if (mountedRef.current && !(kind === 'scale' && zeroingScaleRef.current)) {
          kind === 'analyzer' ? setAnalyzerStatus('disconnected') : setScaleStatus('disconnected');
        }
      }
    }
  };

  const openMachinePort = async (kind, port, rememberSelection = false) => {
    const setStatus = kind === 'analyzer' ? setAnalyzerStatus : setScaleStatus;
    setStatus('connecting');
    try {
      await port.open({ baudRate:kind === 'analyzer' ? analyzerBaud : scaleBaud, dataBits:8, stopBits:1, parity:'none', flowControl:'none' });
      if (kind === 'analyzer') analyzerPortRef.current = port;
      else scalePortRef.current = port;
      if (rememberSelection) {
        const rememberedPorts = await navigator.serial.getPorts();
        const slot = rememberedPorts.indexOf(port) + 1;
        if (slot > 0) localStorage.setItem(kind === 'analyzer' ? 'dp_analyzer_port_slot' : 'dp_scale_port_slot', String(slot));
      }
      setStatus('connected');
      readPort(port, kind);
      return true;
    } catch (e) {
      setStatus('disconnected');
      const isBusy = e.name === 'NetworkError' || e.name === 'InvalidStateError';
      if (e.name !== 'NotFoundError') {
        setError(isBusy
          ? `${kind === 'analyzer' ? 'FAT/SNF port' : 'Weight port'} is already in use. Close the other dairy/serial application, click Clear All Ports, then reconnect.`
          : `Could not connect ${kind}: ${e.message}`);
      }
      return false;
    }
  };

  const connectPort = async (kind) => {
    setError('');
    if (!serialSupported) {
      setError('Machine connection needs Google Chrome or Microsoft Edge on HTTPS/localhost.');
      return false;
    }
    try {
      await disconnectPort(kind);
      const port = await navigator.serial.requestPort();
      return await openMachinePort(kind, port, true);
    } catch (e) {
      const setStatus = kind === 'analyzer' ? setAnalyzerStatus : setScaleStatus;
      setStatus('disconnected');
      if (e.name !== 'NotFoundError') setError(`Could not select ${kind} port: ${e.message}`);
      return false;
    }
  };

  const disconnectPort = async (kind) => {
    const readerRef = kind === 'analyzer' ? analyzerReaderRef : scaleReaderRef;
    const portRef = kind === 'analyzer' ? analyzerPortRef : scalePortRef;
    try { await readerRef.current?.cancel(); } catch {}
    try { await portRef.current?.close(); } catch {}
    readerRef.current = null;
    portRef.current = null;
    kind === 'analyzer' ? setAnalyzerStatus('disconnected') : setScaleStatus('disconnected');
  };

  useEffect(() => {
    if (!serialSupported || autoConnectAttemptedRef.current) return undefined;
    autoConnectAttemptedRef.current = true;
    let cancelled = false;
    const autoConnect = async () => {
      try {
        const ports = await navigator.serial.getPorts();
        if (cancelled || !ports.length) return;
        const analyzerSlot = Math.max(1, Number(localStorage.getItem('dp_analyzer_port_slot')) || 1);
        const scaleSlot = Math.max(1, Number(localStorage.getItem('dp_scale_port_slot')) || 2);
        const analyzerPort = ports[analyzerSlot - 1];
        const scalePort = ports[scaleSlot - 1];
        if (needsAnalyzerPort && analyzerPort) await openMachinePort('analyzer', analyzerPort, false);
        if (!cancelled && needsScalePort && scalePort && scalePort !== analyzerPort) {
          await openMachinePort('scale', scalePort, false);
        }
      } catch (e) {
        if (!cancelled) setError(`Automatic port connection failed: ${e.message}`);
      }
    };
    const timer = setTimeout(autoConnect, 350);
    return () => { cancelled = true; clearTimeout(timer); };
    // Run once per page opening. Port 1 is analyzer and port 2 is weight by default.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnectPrinter = async () => {
    setError('');
    if (typeof window !== 'undefined' && !window.isSecureContext) {
      setError('Printer chooser is blocked because this page is not secure. Open the application using an HTTPS address.');
      return;
    }
    if (!isBluetoothSupported()) {
      setError('Web Bluetooth is unavailable on this PC. Use updated Google Chrome/Edge, turn on the Bluetooth adapter, and check chrome://policy for a blocked Web Bluetooth policy.');
      return;
    }
    try {
      if (navigator.bluetooth.getAvailability && !(await navigator.bluetooth.getAvailability())) {
        setError('Bluetooth is not available on this PC. Turn Bluetooth on, confirm the adapter is enabled in Windows, then try again.');
        return;
      }
    } catch {
      // Continue to requestDevice; Chrome will provide the definitive error.
    }
    setPrinterStatus('connecting');
    const result = await connectPrinter();
    if (result.success) {
      setPrinterStatus('connected');
      setPrinterName(result.name || getPrinterName() || 'Thermal Printer');
      setSuccess(`Printer connected: ${result.name || 'Thermal Printer'}`);
    } else {
      setPrinterStatus('disconnected');
      if (result.error !== 'No printer selected.') setError(result.error || 'Printer connection failed.');
    }
  };

  const handleDisconnectPrinter = () => {
    disconnectPrinter();
    setPrinterStatus('disconnected');
    setPrinterName('');
  };

  const clearAllPorts = async () => {
    setError('');
    setSuccess('');
    try {
      await disconnectPort('analyzer');
      await disconnectPort('scale');
      handleDisconnectPrinter();

      let forgottenCount = 0;
      if (serialSupported && navigator.serial.getPorts) {
        const rememberedPorts = await navigator.serial.getPorts();
        for (const port of rememberedPorts) {
          if (typeof port.forget === 'function') {
            try {
              await port.forget();
              forgottenCount += 1;
            } catch {
              // The connection is already closed; leave unsupported ports disconnected.
            }
          }
        }
      }

      analyzerPortRef.current = null;
      scalePortRef.current = null;
      analyzerReaderRef.current = null;
      scaleReaderRef.current = null;
      setAnalyzerStatus('disconnected');
      setScaleStatus('disconnected');
      localStorage.removeItem('dp_machine_setup_complete');
      localStorage.removeItem('dp_analyzer_port_slot');
      localStorage.removeItem('dp_scale_port_slot');
      setSetupExpanded(true);
      clearReadings();
      setSuccess(forgottenCount > 0
        ? `All ports cleared. ${forgottenCount} remembered port permission${forgottenCount === 1 ? '' : 's'} removed.`
        : 'Both machine ports disconnected and cleared.');
    } catch (e) {
      setError(`Could not clear all ports: ${e.message}`);
    }
  };

  const zeroWeightMachine = async () => {
    const port = scalePortRef.current;
    if (!port) throw new Error('Weight machine is not connected.');
    if (zeroingScaleRef.current) throw new Error('Weight machine zero is already in progress.');
    zeroingScaleRef.current = true;
    setZeroingScale(true);
    setScaleStatus('connecting');
    try {
      // Follow the working desktop sequence: stop reading, close the liter
      // port, reopen it exclusively, write lowercase ASCII z + CR, close, then reconnect.
      const reader = scaleReaderRef.current;
      if (reader) {
        try { await reader.cancel(); } catch {}
        try {
          await Promise.race([
            reader.closed.catch(() => {}),
            new Promise(resolve => setTimeout(resolve, 750)),
          ]);
        } catch {}
      }
      // Wait until readPort's finally block releases Chrome's readable lock.
      for (let attempt = 0; port.readable?.locked && attempt < 75; attempt += 1) {
        await new Promise(resolve => setTimeout(resolve, 10));
      }
      if (port.readable?.locked) throw new Error('Weight port reader did not release. Click Clear All Ports and reconnect it.');
      if (scaleReaderRef.current === reader) scaleReaderRef.current = null;
      await port.close();

      const serialOptions = { baudRate:scaleBaud, dataBits:8, stopBits:1, parity:'none', flowControl:'none' };
      await port.open(serialOptions);
      // Both the manual Zero Scale button and the post-save path use this exact
      // confirmed command, regardless of an older value cached by the browser.
      const data = buildScaleZeroBytes('z');
      const writer = port.writable.getWriter();
      try {
        await writer.write(data);
        await writer.ready;
      } finally {
        writer.releaseLock();
      }
      // Desktop txtSleep.Text is "1", so match Thread.Sleep(1) exactly.
      await new Promise(resolve => setTimeout(resolve, 1));
      await port.close();
      // Give Windows and the USB serial driver time to release the handle before
      // reopening the same physical scale for continuous readings.
      await new Promise(resolve => setTimeout(resolve, 300));
      await port.open(serialOptions);
      scalePortRef.current = port;
      setScaleStatus('connected');
      void readPort(port, 'scale');
    } catch (error) {
      try {
        if (!port.readable && !port.writable) {
          await port.open({ baudRate:scaleBaud, dataBits:8, stopBits:1, parity:'none', flowControl:'none' });
        }
        scalePortRef.current = port;
        setScaleStatus('connected');
        if (port.readable && !scaleReaderRef.current) readPort(port, 'scale');
      } catch {
        scalePortRef.current = null;
        setScaleStatus('disconnected');
      }
      throw error;
    } finally {
      zeroingScaleRef.current = false;
      setZeroingScale(false);
    }
  };

  const handleZeroWeightMachine = async () => {
    setError('');
    try {
      await zeroWeightMachine();
      clearReadings();
      setSuccess('Weight machine zero command sent successfully.');
      setTimeout(() => farmerInputRef.current?.focus(), 80);
    } catch (e) {
      setError(`Could not zero weight machine: ${e.message}`);
    }
  };

  const clearForNextFarmer = () => {
    farmerRef.current = null;
    setFarmerCode(''); setFarmer(null); setLiter(null); setFat(null); setSnf(null); setDensity(null);
    setRate(null); setRateStatus('Waiting for FAT and SNF'); setRawAnalyzer(''); setRawScale('');
    resetStabilityCapture();
    setTimeout(() => farmerInputRef.current?.focus(), 80);
  };

  const clearReadings = () => {
    setLiter(null); setFat(null); setSnf(null); setDensity(null); setRate(null);
    setRateStatus('Waiting for FAT and SNF'); setRawAnalyzer(''); setRawScale('');
    resetStabilityCapture();
  };

  const saveMachineConfiguration = () => {
    if ((needsAnalyzerPort && analyzerStatus !== 'connected') || (needsScalePort && scaleStatus !== 'connected')) {
      setError('Connect the required machine ports before saving the configuration.');
      return;
    }
    localStorage.setItem('dp_machine_setup_complete', '1');
    setSetupWizardOpen(false);
    setSetupExpanded(false);
    setSuccess('Machine configuration saved. The ports will reconnect automatically next time.');
    setTimeout(() => farmerInputRef.current?.focus(), 100);
  };

  const changeConnectionMode = async (mode) => {
    const switchingCombinedMachine = (connectionMode === 'combined') !== (mode === 'combined');
    setConnectionMode(mode);
    if (mode === 'combined') setProfile('combined');
    else if (profile === 'combined') setProfile('ecomilk');
    clearReadings();
    if ((mode === 'weight' || switchingCombinedMachine) && analyzerPortRef.current) await disconnectPort('analyzer');
    if ((mode === 'analyzer' || mode === 'combined') && scalePortRef.current) await disconnectPort('scale');
  };

  const saveCollection = async () => {
    setError(''); setSuccess('');
    if (!canAddCollection) { setError('You do not have permission to add collections.'); return; }
    if (!farmer) { setError('Farmer Code is mandatory.'); return; }
    if (!validReading(liter,0.001,9999)) { setError('Weight / Liter is mandatory.'); return; }
    if (!validReading(fat,1,15)) { setError('FAT is mandatory.'); return; }
    if (!validReading(snf,1,15)) { setError('SNF is mandatory.'); return; }
    if (!(rate > 0)) { setError('Rate must be greater than zero.'); return; }
    if (!(Number(amount) > 0)) { setError('Total Amount is mandatory.'); return; }
    if (!manualEntryActive && !readingConfirmed) { setError('Wait until complete machine readings are received.'); return; }
    setSaving(true);
    try {
      const existingRes = await api.get('/collection', { params:{ date, pageSize:999 } });
      const existing = existingRes.data?.data?.data || [];
      const duplicate = existing.some(x =>
        Number(x.farmerId) === Number(farmer.farmerId) &&
        Number(x.shift) === Number(shift) &&
        Number(x.milkTypeId) === Number(milkTypeId)
      );
      if (duplicate) throw new Error(`${milkTypeId === 2 ? 'Buffalo' : 'Cow'} collection already exists for this farmer and shift.`);

      const payload = {
        farmerId:farmer.farmerId, collectionDate:date, shift, milkTypeId,
        quantity:Number(liter), fat:Number(fat), snf:Number(snf), clr:Number(density || 0),
        rate:Number(Number(rate).toFixed(2)), amount:Number(amount.toFixed(2)),
      };
      const res = await api.post('/collection', payload);
      if (!res.data?.success) throw new Error(res.data?.message || 'Collection was not saved.');

      let printMessage = '';
      if (printerMode === 'windows') {
        const receiptSettings = await getReceiptSettings();
        printCollectionReceipt(
          {
            ...payload,
            code:farmer.farmerCode,
            name:farmer.farmerName,
          },
          centerName,
          receiptSettings.address || '',
          null,
          printerType
        );
        printMessage = ' Receipt sent to the Windows default printer.';
      } else if (isPrinterConnected()) {
        const printResult = await printCollectionSlip({
          centerName,
          centerCode:user?.centerCode || '',
          date:payload.collectionDate,
          shift:payload.shift,
          farmerCode:farmer.farmerCode,
          farmerName:farmer.farmerName,
          milkType:payload.milkTypeId === 2 ? 'Buffalo' : 'Cow',
          quantity:payload.quantity,
          fat:payload.fat,
          snf:payload.snf,
          clr:payload.clr,
          rate:payload.rate,
          amount:payload.amount,
          paperWidth:printerType,
        });
        printMessage = printResult.success ? ' Receipt printed directly.' : ` Print failed: ${printResult.error}`;
      }

      let zeroMessage = '';
      if (needsScalePort && scalePortRef.current?.writable) {
        try {
          await zeroWeightMachine();
          zeroMessage = ' Weight machine reset to zero.';
        } catch (zeroError) {
          zeroMessage = ` Saved, but the weight machine could not be zeroed: ${zeroError.message}`;
        }
      }
      setSuccess(`Saved: ${farmer.farmerCode} - ${farmer.farmerName}, ${fmt(liter,3)} L, Rs.${fmt(amount)}.${printMessage}${zeroMessage}`);
      await loadRecentCollections();
      clearForNextFarmer();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Collection save failed.');
    } finally { setSaving(false); }
  };

  const statusChip = (status) => (
    <Chip size="small" icon={status === 'connected' ? <CheckCircle/> : status === 'connecting' ? <CircularProgress size={13}/> : <ErrorOutline/>}
      label={status === 'connected' ? 'Connected' : status === 'connecting' ? 'Connecting...' : 'Not connected'}
      color={status === 'connected' ? 'success' : status === 'connecting' ? 'warning' : 'default'} variant="outlined" />
  );

  const farmerById = new Map(allFarmers.map(item => [Number(item.farmerId), item]));
  const completedByFarmer = new Map();
  recentCollections.forEach(row => {
    const farmerId = Number(row.farmerId);
    if (!completedByFarmer.has(farmerId)) completedByFarmer.set(farmerId, row);
  });
  const completedFarmers = Array.from(completedByFarmer.values()).map(row => ({
    ...farmerById.get(Number(row.farmerId)),
    ...row,
  }));
  const completedIds = new Set(completedByFarmer.keys());
  const pendingFarmers = allFarmers.filter(item => !completedIds.has(Number(item.farmerId)));
  const progressRows = progressView === 'pending' ? pendingFarmers : completedFarmers;
  const farmerContact = item => item.mobileNumber || item.phoneNumber || '—';

  const ConnectionCard = ({ kind, title, icon, status, baud, setBaud, required, portLabel }) => (
    <Card variant="outlined" sx={{ p:2, borderRadius:2.5, borderColor:status === 'connected'?'#86efac':'#e2e8f0' }}>
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:1, mb:1.5, flexWrap:'wrap' }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          {icon}<Typography sx={{ fontWeight:800 }}>{title}</Typography>
          <Chip label={portLabel} size="small" variant="outlined" sx={{ height:20, fontSize:'0.65rem' }}/>
          {required && <Chip label="Required" size="small" color="warning" sx={{ height:20, fontSize:'0.65rem' }}/>} 
        </Box>
        {statusChip(status)}
      </Box>
      <Box sx={{ display:'flex', gap:1, alignItems:'center' }}>
        <FormControl size="small" sx={{ minWidth:120 }} disabled={status === 'connected'}>
          <InputLabel>Baud Rate</InputLabel>
          <Select value={baud} label="Baud Rate" onChange={e => setBaud(Number(e.target.value))}>
            {BAUD_RATES.map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
          </Select>
        </FormControl>
        {status === 'connected' ?
          <>
            {kind === 'scale' && <Button variant="outlined" color="warning" disabled={zeroingScale} onClick={handleZeroWeightMachine}>{zeroingScale ? 'Zeroing…' : 'Zero Scale'}</Button>}
            <Button variant="outlined" color="error" onClick={() => disconnectPort(kind)}>Disconnect</Button>
          </> :
          <Button variant="contained" startIcon={<Cable/>} disabled={status === 'connecting'} onClick={() => connectPort(kind)}
            sx={{ background:`linear-gradient(135deg,${theme.primary},${theme.hover})` }}>Select Port & Connect</Button>}
      </Box>
    </Card>
  );

  return (
    <Layout title="Automatic Collection" hideNavigation>
      <Dialog open={setupWizardOpen} maxWidth="sm" fullWidth onClose={() => setSetupWizardOpen(false)}>
        <DialogTitle sx={{ fontWeight:900, display:'flex', alignItems:'center', justifyContent:'space-between', gap:1 }}>
          First-Time Machine Setup
          <IconButton aria-label="Close machine setup" onClick={() => setSetupWizardOpen(false)}>
            <Close/>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ color:'#475569', mb:2 }}>
            Select each machine port once. Chrome will remember your choices and reconnect them automatically on future visits.
          </Typography>
          {!serialSupported && (
            <Alert severity="warning" sx={{ mb:2 }}>Open this screen in Google Chrome or Microsoft Edge using HTTPS.</Alert>
          )}
          <FormControl fullWidth size="small" sx={{ mb:1.5 }}>
            <InputLabel>Connected Machines</InputLabel>
            <Select value={connectionMode} label="Connected Machines" onChange={event => changeConnectionMode(event.target.value)}>
              {CONNECTION_MODES.map(item => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
            </Select>
          </FormControl>
          <Alert severity="info" sx={{ mb:1.5 }}>{selectedConnectionMode.help}</Alert>
          {needsAnalyzerPort && connectionMode !== 'combined' && (
            <FormControl fullWidth size="small" sx={{ mb:1.5 }}>
              <InputLabel>Analyzer Type</InputLabel>
              <Select value={profile} label="Analyzer Type" onChange={event => setProfile(event.target.value)}>
                {MACHINE_PROFILES.filter(item => item.value !== 'combined').map(item => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {needsAnalyzerPort && <Card variant="outlined" sx={{ p:1.5, mb:needsScalePort?1.5:0, borderRadius:2.5 }}>
            <Box sx={{ display:'flex', alignItems:'center', gap:1, flexWrap:'wrap' }}>
              <Science sx={{ color:theme.primary }}/>
              <Typography sx={{ fontWeight:900, flex:1 }}>{needsScalePort ? '1. ' : ''}{connectionMode === 'combined' ? 'Combined AMCS Machine' : 'FAT / SNF Analyzer'}</Typography>
              <Chip size="small" label={analyzerStatus === 'connected' ? 'Connected' : analyzerStatus === 'connecting' ? 'Connecting…' : 'Not connected'} color={analyzerStatus === 'connected' ? 'success' : 'default'} variant="outlined" />
            </Box>
            <Box sx={{ display:'flex', gap:1, mt:1.25, flexWrap:'wrap' }}>
              <FormControl size="small" sx={{ minWidth:130 }}>
                <InputLabel>Baud Rate</InputLabel>
                <Select value={analyzerBaud} label="Baud Rate" disabled={analyzerStatus === 'connected'} onChange={event => setAnalyzerBaud(Number(event.target.value))}>
                  {BAUD_RATES.map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                </Select>
              </FormControl>
              <Button variant="contained" startIcon={<Cable/>} disabled={!serialSupported || analyzerStatus === 'connecting'} onClick={() => connectPort('analyzer')}>
                {analyzerStatus === 'connected' ? 'Change FAT Port' : 'Select FAT Port'}
              </Button>
            </Box>
          </Card>}
          {needsScalePort && (
            <Card variant="outlined" sx={{ p:1.5, borderRadius:2.5 }}>
              <Box sx={{ display:'flex', alignItems:'center', gap:1, flexWrap:'wrap' }}>
                <Scale sx={{ color:theme.primary }}/>
                <Typography sx={{ fontWeight:900, flex:1 }}>{needsAnalyzerPort ? '2. ' : ''}Liter / Weighing Machine</Typography>
                <Chip size="small" label={scaleStatus === 'connected' ? 'Connected' : scaleStatus === 'connecting' ? 'Connecting…' : 'Not connected'} color={scaleStatus === 'connected' ? 'success' : 'default'} variant="outlined" />
              </Box>
              <Box sx={{ display:'flex', gap:1, mt:1.25, flexWrap:'wrap' }}>
                <FormControl size="small" sx={{ minWidth:130 }}>
                  <InputLabel>Baud Rate</InputLabel>
                  <Select value={scaleBaud} label="Baud Rate" disabled={scaleStatus === 'connected'} onChange={event => setScaleBaud(Number(event.target.value))}>
                    {BAUD_RATES.map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                  </Select>
                </FormControl>
                <Button variant="contained" startIcon={<Cable/>} disabled={!serialSupported || scaleStatus === 'connecting'} onClick={() => connectPort('scale')}>
                  {scaleStatus === 'connected' ? 'Change Weight Port' : 'Select Weight Port'}
                </Button>
              </Box>
            </Card>
          )}
        </DialogContent>
        <DialogActions sx={{ px:3, py:1.5 }}>
          <Button color="inherit" onClick={() => setSetupWizardOpen(false)}>Configure Later</Button>
          <Button
            variant="contained"
            startIcon={<Save/>}
            disabled={(needsAnalyzerPort && analyzerStatus !== 'connected') || (needsScalePort && scaleStatus !== 'connected')}
            onClick={saveMachineConfiguration}
          >
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>
      {!serialSupported && <Alert severity="warning" sx={{ mb:2 }}>Use Google Chrome or Microsoft Edge. Web Serial machine connection is not supported by this browser.</Alert>}
      {error && <Alert severity="error" sx={{ mb:2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb:2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Card sx={{ borderRadius:3, boxShadow:'0 2px 14px rgba(15,23,42,0.08)', overflow:'hidden' }}>
        <Box sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr', lg:'minmax(0,0.78fr) minmax(0,1.22fr)' }, alignItems:'start' }}>
          <Box>
        <Box sx={{ px:{ xs:2, md:3 }, py:1.25, background:`linear-gradient(135deg,${theme.primary},${theme.hover})`, color:'#fff', display:'flex', alignItems:'center', gap:1.5 }}>
          <PrecisionManufacturing/>
          <Box sx={{ flex:1 }}>
            <Typography variant="h6" sx={{ fontWeight:900 }}>Automatic Milk Collection</Typography>
            <Typography sx={{ fontSize:'0.78rem', opacity:0.88 }}>Connected machines fill automatically. Disconnected machine values can be entered manually.</Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBack/>}
            onClick={() => navigate('/dashboard')}
            sx={{ color:'#fff', borderColor:'rgba(255,255,255,.75)', whiteSpace:'nowrap', '&:hover':{ borderColor:'#fff', background:'rgba(255,255,255,.12)' } }}
          >
            Dashboard
          </Button>
        </Box>

        <Box sx={{ p:{ xs:2, md:2 } }}>
            <Box sx={{ mb:2, p:{ xs:1.1, md:1.25 }, border:'1px solid #e2e8f0', borderRadius:2.5, background:'#f8fafc' }}>
              <Box sx={{ display:'grid', gridTemplateColumns:'minmax(0,1fr) auto', alignItems:'start', gap:0.75, pb:0.15 }}>
                <Box sx={{ display:'flex', alignItems:'center', gap:0.65, flexWrap:'wrap', minWidth:0 }}>
                  <Typography sx={{ fontWeight:900, flexShrink:0 }}>Machine Setup</Typography>
                  {!setupExpanded && <>
                    <Chip size="small" label={selectedConnectionMode.label} variant="outlined" />
                    {needsAnalyzerPort && <Chip size="small" label={`${connectionMode === 'combined' ? 'Combined' : selectedProfile.shortLabel} ${analyzerStatus==='connected'?'ON':analyzerStatus==='connecting'?'...':'OFF'}`} color={analyzerStatus==='connected'?'success':'default'} variant="outlined" />}
                    {needsScalePort && <Chip size="small" label={`Weight ${scaleStatus==='connected'?'ON':scaleStatus==='connecting'?'...':'OFF'}`} color={scaleStatus==='connected'?'success':'default'} variant="outlined" />}
                    <Chip
                      size="small"
                      label={printerMode === 'windows' ? 'Printer WINDOWS' : `Printer ${printerStatus==='connected'?'ON':printerStatus==='connecting'?'...':'OFF'}`}
                      color={printerMode === 'windows' || printerStatus === 'connected' ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </>}
                </Box>
                <Button
                  size="small"
                  variant={setupExpanded?'text':'outlined'}
                  endIcon={setupExpanded?<ExpandLess/>:<ExpandMore/>}
                  onClick={() => setSetupExpanded(value => !value)}
                  sx={{ flexShrink:0, whiteSpace:'nowrap', alignSelf:'start' }}
                >
                  {setupExpanded ? 'Hide Setup' : 'Show Setup'}
                </Button>
              </Box>
              <Collapse in={setupExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ mt:1.5, display:'grid', gridTemplateColumns:'1fr', gap:1.2, alignItems:'stretch' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Connected Machines</InputLabel>
                <Select value={connectionMode} label="Connected Machines" onChange={event => changeConnectionMode(event.target.value)}>
                  {CONNECTION_MODES.map(item => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
                </Select>
              </FormControl>
                <Alert severity="info">{selectedConnectionMode.help}</Alert>
                {needsAnalyzerPort && connectionMode !== 'combined' && (
                  <FormControl fullWidth size="small">
                    <InputLabel>Analyzer Type</InputLabel>
                    <Select value={profile} label="Analyzer Type" onChange={event => { setProfile(event.target.value); clearReadings(); }}>
                      {MACHINE_PROFILES.filter(item => item.value !== 'combined').map(item => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
                {needsAnalyzerPort && <ConnectionCard kind="analyzer" title={connectionMode === 'combined' ? 'Combined AMCS Machine' : 'FAT / SNF Analyzer'} portLabel="Saved Port 1" icon={<Science sx={{ color:theme.primary }}/>} status={analyzerStatus} baud={analyzerBaud} setBaud={setAnalyzerBaud} required />}
                {needsScalePort && <ConnectionCard kind="scale" title="Liter / Weighing Machine" portLabel="Saved Port 2" icon={<Scale sx={{ color:theme.primary }}/>} status={scaleStatus} baud={scaleBaud} setBaud={setScaleBaud} required />}
                {needsScalePort && (
                  <TextField
                    size="small"
                    label="Weight Machine Zero Command"
                    value={scaleZeroCommand}
                    onChange={event => setScaleZeroCommand(event.target.value)}
                    helperText={'Default sends lowercase z followed by carriage return.'}
                  />
                )}
                <Card variant="outlined" sx={{ p:1.5, borderRadius:2.5 }}>
                  <Box sx={{ display:'flex', alignItems:'center', gap:1, flexWrap:'wrap' }}>
                    <Print sx={{ color:theme.primary }}/>
                    <Typography sx={{ fontWeight:900, mr:'auto' }}>Thermal Printer</Typography>
                    <Chip
                      size="small"
                      label={printerMode === 'windows' ? 'Windows default printer' : printerStatus === 'connected' ? (printerName || 'Connected') : printerStatus === 'connecting' ? 'Connecting…' : 'Not connected'}
                      color={printerMode === 'windows' || printerStatus === 'connected' ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ mt:1.2, display:'flex', alignItems:'center', gap:1, flexWrap:'wrap' }}>
                    <FormControl size="small" sx={{ minWidth:210 }}>
                      <InputLabel>Printer Connection</InputLabel>
                      <Select value={printerMode} label="Printer Connection" onChange={event => setPrinterMode(event.target.value)}>
                        <MenuItem value="windows">Windows USB — Default Printer</MenuItem>
                        <MenuItem value="bluetooth">Bluetooth BLE Printer</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth:150 }}>
                      <InputLabel>Print Type</InputLabel>
                      <Select value={printerType} label="Print Type" onChange={event => setPrinterType(event.target.value)}>
                        <MenuItem value="2inch">2 Inch Thermal</MenuItem>
                        <MenuItem value="3inch">3 Inch Thermal</MenuItem>
                      </Select>
                    </FormControl>
                    {printerMode === 'windows' ? (
                      <Chip size="small" color="success" variant="outlined" label="Ready — uses Windows default" />
                    ) : printerStatus === 'connected' ? (
                      <Button variant="outlined" color="error" onClick={handleDisconnectPrinter}>Disconnect Printer</Button>
                    ) : (
                      <Button
                        variant="contained"
                        startIcon={<Print/>}
                        disabled={printerStatus === 'connecting'}
                        onClick={handleConnectPrinter}
                        sx={{ background:`linear-gradient(135deg,${theme.primary},${theme.hover})` }}
                      >
                        Select Printer & Connect
                      </Button>
                    )}
                  </Box>
                  <Typography sx={{ mt:0.9, fontSize:'0.72rem', color:'#64748b' }}>
                    Windows USB mode prints through the default Windows printer. Bluetooth mode is only for BLE/GATT ESC/POS printers.
                  </Typography>
                </Card>
              </Box>
              <Alert severity="info" sx={{ mt:1.5, fontSize:'0.76rem' }}>When the browser asks, choose the COM/USB port belonging to that machine. The browser never sends serial data anywhere except this collection screen.</Alert>
              </Collapse>
            </Box>

            <Box>
              <Typography sx={{ fontWeight:900, mb:0.7 }}>1. Collection Entry</Typography>
              <Box sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr', sm:'160px 1fr 150px' }, gap:1, mb:1 }}>
                <TextField inputRef={farmerInputRef} label="Farmer Code" value={farmerCode} onChange={handleFarmerChange}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); lookupFarmer(farmerCode); } }}
                  autoFocus size="small" inputProps={{ autoComplete:'off' }}
                  InputProps={{ endAdornment:loadingFarmer?<CircularProgress size={17}/>:null }} />
                <Box sx={{ px:1.4, py:1, minWidth:0, display:'flex', alignItems:'center', borderRadius:2, background:farmer?'#ecfdf5':'#f8fafc', border:`1px solid ${farmer?'#86efac':'#e2e8f0'}` }}>
                  <Typography noWrap title={farmer ? farmer.farmerName : 'Farmer details appear automatically — enter exact farmer code'} sx={{ fontWeight:800, color:farmer?'#166534':'#94a3b8', minWidth:0, overflow:'hidden', textOverflow:'ellipsis' }}>
                    {farmer ? `${farmer.farmerName} • ${farmer.milkTypeName || (milkTypeId===2?'Buffalo':'Cow')} • ${farmer.mobileNumber || farmer.phoneNumber || 'No mobile'}` : 'Farmer details appear automatically — enter exact farmer code'}
                  </Typography>
                </Box>
                <TextField type="date" label="Collection Date" value={date} onChange={e => setDate(e.target.value)} size="small" inputProps={{ max:today() }} InputLabelProps={{ shrink:true }}/>
              </Box>

              <Box sx={{ display:'flex', gap:1, mb:1, flexWrap:'wrap' }}>
                <Button startIcon={<WbSunny/>} variant={shift===1?'contained':'outlined'} onClick={() => setShift(1)} color="warning">Morning</Button>
                <Button startIcon={<NightsStay/>} variant={shift===2?'contained':'outlined'} onClick={() => setShift(2)}>Evening</Button>
                <Button variant={milkTypeId===1?'contained':'outlined'} onClick={() => setMilkTypeId(1)}>Cow</Button>
                <Button variant={milkTypeId===2?'contained':'outlined'} onClick={() => setMilkTypeId(2)}>Buffalo</Button>
              </Box>

              <Box sx={{ display:'grid', gridTemplateColumns:{ xs:'repeat(2,1fr)', sm:'repeat(3,1fr)' }, gap:1 }}>
                <ReadingTile label="LITER" value={fmt(liter,3)} rawValue={liter} unit="L" ready={validReading(liter,0.001,9999)} icon={<Scale sx={{ color:'#64748b' }}/>} editable={manualLiterEntry} disabled={!farmer} inputRef={literInputRef} onChange={changeManualReading('liter',setLiter)} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); manualAnalyzerEntry ? fatInputRef.current?.focus() : saveButtonRef.current?.focus(); } }} step="0.001"/>
                <ReadingTile label="FAT" value={fmt(fat,1)} rawValue={fat} unit="%" ready={validReading(fat,1,15)} icon={<Science sx={{ color:'#64748b' }}/>} editable={manualAnalyzerEntry} disabled={!farmer} inputRef={fatInputRef} onChange={changeManualReading('fat',setFat)} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); snfInputRef.current?.focus(); } }} step="0.1"/>
                <ReadingTile label="SNF" value={fmt(snf,1)} rawValue={snf} unit="%" ready={validReading(snf,1,15)} icon={<Science sx={{ color:'#64748b' }}/>} editable={manualAnalyzerEntry} disabled={!farmer} inputRef={snfInputRef} onChange={changeManualReading('snf',setSnf)} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); saveButtonRef.current?.focus(); } }} step="0.1"/>
                <ReadingTile label="DENSITY" value={fmt(density,2)} unit="" ready={validReading(density,0,100)} icon={<Science sx={{ color:'#64748b' }}/>}/>
                <ReadingTile label="RATE" value={`₹${fmt(rate)}`} unit="/L" ready={rate>0} icon={<CheckCircle sx={{ color:'#64748b' }}/>}/>
                <ReadingTile label="AMOUNT" value={`₹${fmt(amount)}`} unit="" ready={amount>0} icon={<CheckCircle sx={{ color:'#64748b' }}/>}/>
              </Box>

              <Box sx={{ mt:1, px:1.25, py:0.75, borderRadius:2, background:'#f8fafc', border:'1px solid #e2e8f0' }}>
                <Typography sx={{ fontSize:'0.76rem', fontWeight:800, color:rate>0?'#166534':'#b45309', mb:0.5 }}>
                  {rateStatus}
                </Typography>
                <Box component="details" sx={{ mt:0.2 }}>
                  <Typography component="summary" sx={{ fontSize:'0.7rem', color:'#64748b', cursor:'pointer', userSelect:'none' }}>Machine raw data</Typography>
                  <Typography sx={{ fontSize:'0.68rem', color:'#64748b', mt:0.25, wordBreak:'break-all' }}>Analyzer: {rawAnalyzer || 'Waiting for machine data...'}</Typography>
                  {needsScalePort && <Typography sx={{ fontSize:'0.68rem', color:'#64748b', mt:0.15, wordBreak:'break-all' }}>Scale: {rawScale || 'Waiting for liter data...'}</Typography>}
                </Box>
              </Box>

              <Box sx={{ display:'flex', justifyContent:'flex-end', alignItems:'center', mt:1, gap:1, flexWrap:'wrap' }}>
                <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
                  <Chip
                    label={entryConfirmed?(manualEntryActive?'Manual entry ready':'Reading confirmed'):allReadingsReady?'Reading ready':'Waiting for complete data'}
                    color={entryConfirmed?'success':allReadingsReady?'warning':'default'}
                    variant="outlined"
                  />
                  <Button ref={saveButtonRef} variant="contained" size="large" startIcon={saving?<CircularProgress size={18} color="inherit"/>:<Save/>} disabled={!farmer || saving || !canAddCollection} onClick={saveCollection}
                    sx={{ minWidth:180, fontWeight:900, background:`linear-gradient(135deg,${theme.primary},${theme.hover})` }}>
                    {saving ? 'Saving...' : t('save') + ' Collection'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
            </Box>
            <Box sx={{ p:{ xs:2, md:2 }, background:'#fff' }}>
            <CustomerDisplay
              farmerCode={farmer?.farmerCode || farmerCode}
              farmerName={farmer?.farmerName}
              liter={liter}
              fat={fat}
              snf={snf}
              density={density}
              rate={rate}
              amount={amount}
              onClear={clearReadings}
              onClearPorts={clearAllPorts}
              onConnectPrinter={handleConnectPrinter}
              serialSupported={serialSupported}
              printerStatus={printerStatus}
              printerName={printerName}
              printerMode={printerMode}
            />
          </Box>
        </Box>
      </Card>

      <Card sx={{ mt:2, borderRadius:3, boxShadow:'0 2px 14px rgba(15,23,42,0.08)', overflow:'hidden' }}>
        <Box sx={{ px:2, py:1.25, display:'flex', alignItems:'center', justifyContent:'space-between', gap:1, flexWrap:'wrap', background:'#f8fafc', borderBottom:'1px solid #e2e8f0' }}>
          <Box>
            <Typography sx={{ fontWeight:900 }}>Collection Progress — {date} — {shift === 1 ? 'Morning' : 'Evening'}</Typography>
            <Typography sx={{ fontSize:'0.75rem', color:'#64748b' }}>Completed and pending farmers include their saved contact number.</Typography>
          </Box>
          <Button size="small" startIcon={<Refresh/>} onClick={loadRecentCollections}>Refresh</Button>
        </Box>

        <Box sx={{ p:1.5, display:'grid', gridTemplateColumns:{ xs:'1fr', sm:'repeat(3,1fr)' }, gap:1 }}>
          <Box sx={{ p:1.25, borderRadius:2, background:'#eff6ff', border:'1px solid #bfdbfe', textAlign:'center' }}>
            <Typography sx={{ fontSize:'0.72rem', color:'#1d4ed8', fontWeight:800 }}>TOTAL FARMERS</Typography>
            <Typography sx={{ fontSize:'1.7rem', color:'#1e3a8a', fontWeight:900 }}>{allFarmers.length}</Typography>
          </Box>
          <Box onClick={() => setProgressView('completed')} sx={{ p:1.25, borderRadius:2, background:'#ecfdf5', border:`2px solid ${progressView==='completed'?'#16a34a':'#bbf7d0'}`, textAlign:'center', cursor:'pointer' }}>
            <Typography sx={{ fontSize:'0.72rem', color:'#15803d', fontWeight:800 }}>COMPLETED</Typography>
            <Typography sx={{ fontSize:'1.7rem', color:'#166534', fontWeight:900 }}>{completedFarmers.length} / {allFarmers.length}</Typography>
          </Box>
          <Box onClick={() => setProgressView('pending')} sx={{ p:1.25, borderRadius:2, background:'#fff7ed', border:`2px solid ${progressView==='pending'?'#ea580c':'#fed7aa'}`, textAlign:'center', cursor:'pointer' }}>
            <Typography sx={{ fontSize:'0.72rem', color:'#c2410c', fontWeight:800 }}>PENDING</Typography>
            <Typography sx={{ fontSize:'1.7rem', color:'#9a3412', fontWeight:900 }}>{pendingFarmers.length} / {allFarmers.length}</Typography>
          </Box>
        </Box>

        <Box sx={{ px:1.5, pb:1, display:'flex', gap:1 }}>
          <Button variant={progressView==='pending'?'contained':'outlined'} color="warning" onClick={() => setProgressView('pending')}>Pending ({pendingFarmers.length})</Button>
          <Button variant={progressView==='completed'?'contained':'outlined'} color="success" onClick={() => setProgressView('completed')}>Completed ({completedFarmers.length})</Button>
        </Box>

        <TableContainer sx={{ maxHeight:360 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight:900 }}>Code</TableCell>
                <TableCell sx={{ fontWeight:900 }}>Farmer</TableCell>
                <TableCell sx={{ fontWeight:900 }}>Contact Number</TableCell>
                <TableCell sx={{ fontWeight:900 }}>Milk</TableCell>
                {progressView === 'completed' && <TableCell align="right" sx={{ fontWeight:900 }}>Liter</TableCell>}
                {progressView === 'completed' && <TableCell align="right" sx={{ fontWeight:900 }}>Amount</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {progressRows.length === 0 ? (
                <TableRow><TableCell colSpan={progressView === 'completed' ? 6 : 4} align="center" sx={{ color:'#64748b', py:3 }}>
                  {progressView === 'pending' ? 'No pending farmers.' : 'No completed collections for this date and shift.'}
                </TableCell></TableRow>
              ) : progressRows.map((row, index) => (
                <TableRow key={row.collectionId || row.farmerId || index} hover>
                  <TableCell sx={{ fontWeight:800, color:theme.primary }}>{row.farmerCode || row.code || '—'}</TableCell>
                  <TableCell>{row.farmerName || row.name || '—'}</TableCell>
                  <TableCell sx={{ fontWeight:700 }}>{farmerContact(row)}</TableCell>
                  <TableCell>{row.milkTypeName || (Number(row.milkTypeId) === 2 ? 'Buffalo' : 'Cow')}</TableCell>
                  {progressView === 'completed' && <TableCell align="right">{fmt(row.quantity,3)}</TableCell>}
                  {progressView === 'completed' && <TableCell align="right">{fmt(row.amount,2)}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

    </Layout>
  );
}
